import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Alert,
  useTheme,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/authService';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    // Basic email validation
    if (!email.includes('@')) {
      setError('Por favor, insira um email válido');
      return;
    }

    setIsLoading(true);

    try {
      // Pass the full email - AuthContext will handle the normalization
      const success = await login(email, password);

      if (success) {
        // Redirect to dashboard after successful login
        navigate('/dashboard');
      } else {
        setError('Email ou senha inválidos');
      }
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      setError('Ocorreu um erro ao tentar entrar. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        width: '100%',
        px: { xs: 2, sm: 3 },
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4 },
          width: '100%',
          maxWidth: '450px',
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <Box
            component="img"
            src="/logo.png"
            alt="Logo Clinafy"
            loading="lazy"
            sx={{
              width: { xs: 90, sm: 120 },
              height: { xs: 90, sm: 120 },
              borderRadius: '50%',
              objectFit: 'cover',
              mb: 2,
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
          />
          <Typography
            variant="h5"
            component="h1"
            align="center"
            gutterBottom
            sx={{
              fontSize: { xs: '1.4rem', sm: '1.5rem', md: '1.8rem' },
              fontWeight: 600
            }}
          >
            Clinafy
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            Sistema de Gestão para Clínicas
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            autoComplete="email"
            placeholder="seu@email.com"
            sx={{ mb: 1 }}
            InputProps={{
              sx: { borderRadius: 1.5 }
            }}
          />

          <TextField
            label="Senha"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            sx={{ mb: 2 }}
            InputProps={{
              sx: { borderRadius: 1.5 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Box sx={{ textAlign: 'right', mt: 0.5, mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#1B6A63',
                cursor: 'pointer',
                fontSize: '0.85rem',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={() => {
                setShowForgotPassword(true);
                setResetEmail(email);
                setResetSent(false);
                setResetError('');
              }}
            >
              Esqueci minha senha
            </Typography>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              mt: 2,
              mb: 1,
              py: { xs: 1.2, sm: 1.4 },
              borderRadius: 1.5,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 500,
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.dark
              }
            }}
          >
            {isLoading ? 'Entrando...' : 'Entrar'}
          </Button>

          {/* Signup link */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Não tem uma conta?{' '}
              <Link
                to="/signup?trial=true"
                style={{
                  color: '#1B6A63',
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Cadastre-se grátis
              </Link>
            </Typography>
          </Box>

          {/* Back to site link */}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link
              to="/"
              style={{
                color: theme.palette.text.secondary,
                fontSize: '0.875rem',
                textDecoration: 'none',
              }}
            >
              ← Voltar ao site
            </Link>
          </Box>
        </Box>
      </Paper>

      {/* Forgot Password Dialog */}
      <Dialog
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Redefinir Senha
        </DialogTitle>
        <DialogContent>
          {resetSent ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Se este email estiver cadastrado, você receberá um link para redefinir sua senha.
              Verifique sua caixa de entrada e spam.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                Informe seu email cadastrado. Enviaremos um link para redefinição de senha.
              </Typography>
              {resetError && (
                <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>
              )}
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                autoFocus
                placeholder="seu@email.com"
                InputProps={{
                  sx: { borderRadius: 1.5 }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowForgotPassword(false)}
            color="inherit"
          >
            {resetSent ? 'Fechar' : 'Cancelar'}
          </Button>
          {!resetSent && (
            <Button
              variant="contained"
              disabled={resetLoading || !resetEmail}
              onClick={async () => {
                setResetLoading(true);
                setResetError('');
                try {
                  const result = await authService.resetPassword(resetEmail);
                  if (result.success) {
                    setResetSent(true);
                  } else {
                    setResetError(result.error || 'Erro ao enviar email');
                  }
                } catch {
                  setResetError('Erro ao processar solicitação');
                } finally {
                  setResetLoading(false);
                }
              }}
              sx={{
                bgcolor: theme.palette.primary.main,
                '&:hover': { bgcolor: theme.palette.primary.dark }
              }}
            >
              {resetLoading ? <CircularProgress size={24} color="inherit" /> : 'Enviar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LoginForm;
